import React from "react";
import clearIconLogo from "@/images/clear_logo.svg";
import clearWordmarkLogo from "@images/clear_logo_horizontal.png";
import VerificationIcon from "@/sprout/icons/verification";
import { cn } from "@/lib/utils";
import { identityVerificationPartnerConfig } from "@/utils/identity_verification_provider";

type IdentityVerificationLogoVariant = "icon" | "wordmark";
type IdentityVerificationLogoSize = "sm" | "md";

type Props = {
  partner?: string | null;
  variant?: IdentityVerificationLogoVariant;
  size?: IdentityVerificationLogoSize;
  className?: string;
  decorative?: boolean;
};

const CLEAR_LOGO_CLASSES: Record<
  IdentityVerificationLogoVariant,
  Record<IdentityVerificationLogoSize, string>
> = {
  icon: {
    sm: "h-5 w-5 object-contain",
    md: "h-6 w-6 object-contain",
  },
  wordmark: {
    sm: "h-6 w-auto",
    md: "h-8 w-auto",
  },
};

const PERSONA_WORDMARK_CLASSES: Record<IdentityVerificationLogoSize, string> = {
  sm: "text-[20px] leading-6",
  md: "text-[22px] leading-8",
};

const PERSONA_ICON_SIZE: Record<IdentityVerificationLogoSize, "sm" | "md"> = {
  sm: "sm",
  md: "md",
};

const IdentityVerificationLogo: React.FC<Props> = ({
  partner,
  variant = "wordmark",
  size = "sm",
  className = "",
  decorative = false,
}) => {
  const partnerConfig = identityVerificationPartnerConfig(partner);
  const altText = decorative ? "" : `${partnerConfig.name} logo`;

  switch (partnerConfig.partner) {
    case "clear":
      return (
        <img
          src={variant === "icon" ? clearIconLogo : clearWordmarkLogo}
          alt={altText}
          aria-hidden={decorative ? true : undefined}
          className={cn(CLEAR_LOGO_CLASSES[variant][size], className)}
        />
      );
    case "persona":
      if (variant === "icon") {
        return (
          <span
            aria-hidden={decorative ? true : undefined}
            aria-label={decorative ? undefined : partnerConfig.name}
            className={cn("inline-flex items-center justify-center text-green-700", className)}
          >
            <VerificationIcon ariaHidden={decorative} size={PERSONA_ICON_SIZE[size]} />
          </span>
        );
      }

      return (
        <span
          aria-hidden={decorative ? true : undefined}
          className={cn(
            "inline-flex items-center font-medium text-green-700",
            PERSONA_WORDMARK_CLASSES[size],
            className,
          )}
        >
          {partnerConfig.displayName}
        </span>
      );
  }
};

export default IdentityVerificationLogo;
